import React from "react";
import ReactDOM from "react-dom";

// One z-index for every overlay. A modal opened from another one stacks above it because its
// portal is appended to document.body after the first one's. Toast (1100) and InfoTooltip
// (10000) stay above every overlay.
export const MODAL_Z_INDEX = 1000;

// The panel sets the app's ink: a portal renders under <body>, outside every screen that set it.
const BACKDROP = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: MODAL_Z_INDEX, display: "flex" };
const INK = "#151515";

const LOOKS = {
  center: {
    backdrop: { justifyContent: "center", alignItems: "center", padding: 16 },
    panel: {
      position: "relative", background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 20,
      maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    },
  },
  drawer: {
    backdrop: { justifyContent: "flex-end", alignItems: "stretch" },
    panel: {
      position: "relative", background: "#FFFFFF", borderLeft: "1px solid #E2E2E2", height: "100%",
      boxShadow: "-6px 0 28px rgba(0,0,0,0.12)",
    },
  },
};

// The backdrop and panel of one overlay, for the `motion` object its owner got from
// useModalMotion (the owner keeps the hook, so its own success paths can call requestClose).
// The layout follows the hook's variant: "center" (a modal) or "drawer" (a side panel).
//   label / labelledBy   names the dialog (aria-label, or the id of its visible title); pass one
//   width / maxWidth     panel sizing; a center panel is width 100% up to maxWidth
//   panelStyle           the panel's own content styles (padding, overflow, flex layout, height)
//   portal               false renders in place instead of into document.body
//   backdropRef          a ref on the backdrop (an outside-click check that must count it in)
export function ModalShell({
  motion,
  label,
  labelledBy,
  width,
  maxWidth,
  panelStyle,
  portal = true,
  backdropRef,
  children,
}) {
  const variant = motion.variant;
  const look = LOOKS[variant];
  const backdropStyle = { ...BACKDROP, ...look.backdrop };
  const sizing = variant === "drawer" ? { width, maxWidth } : { width: width ?? "100%", maxWidth };
  // A panelStyle `overflow` replaces the default overflowY (React must not mix the shorthand
  // and its longhand on one element).
  const { overflowY, ...panelBase } = look.panel;
  const panelDefaults = panelStyle && "overflow" in panelStyle ? panelBase : look.panel;
  const shell = (
    <div ref={backdropRef} {...motion.backdropProps} style={backdropStyle}>
      <div
        {...motion.panelProps}
        aria-label={label}
        aria-labelledby={label ? undefined : labelledBy}
        style={{ color: INK, ...panelDefaults, ...sizing, ...panelStyle }}
      >
        {children}
      </div>
    </div>
  );
  return portal ? ReactDOM.createPortal(shell, document.body) : shell;
}
